import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // Paper,
  CircularProgress,
  Alert,
  Skeleton,
  Fade,
  Tooltip,
  alpha,
  useTheme,
  Divider,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Container,
  Avatar,
  Zoom,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApprovedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  // Inventory as InventoryIcon,
  // TrendingUp,
  ErrorOutline,
  Scanner as ScannerIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CheckCircle,
  Assessment,
  Security,
  Speed,
  FilterList,
  Inventory2
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

interface AIAsset {
  id: number;
  asset_type: string;
  name: string;
  description: string;
  version: string;
  provider: string;
  model_identifier: string;
  status: string;
  risk_level: string;
  is_approved: boolean;
  usage_count: number;
  last_used: string;
  Owner?: { email: string };
  Team?: { name: string };
}

interface DashboardStats {
  totalAssets: number;
  assetsByType: Array<{ asset_type: string; count: number }>;
  assetsByStatus: Array<{ status: string; count: number }>;
  assetsByRisk: Array<{ risk_level: string; count: number }>;
  unapprovedAssets: number;
}

const AIInventory: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [assets, setAssets] = useState<AIAsset[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    assetType: '',
    status: '',
    provider: '',
    riskLevel: '',
    isApproved: ''
  });

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AIAsset | null>(null);
  const [formData, setFormData] = useState({
    asset_type: 'model',
    name: '',
    description: '',
    version: '',
    provider: '',
    model_identifier: '',
    server_name: '',
    server_identifier: '',
    token_cost: '',
    status: 'active',
    risk_level: 'low'
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [assetsRes, statsRes] = await Promise.all([
        axios.get('/api/ai-inventory', {
          headers: { Authorization: `Bearer ${token}` },
          params: filters
        }),
        axios.get('/api/ai-inventory/dashboard/stats', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAssets(assetsRes.data.data || []);
      setStats(statsRes.data.data);
    } catch (error: any) {
      console.error('Failed to load inventory:', error);
      toast.error(error.response?.data?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleRunScan = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/ai-inventory/scans/run',
        { scanType: 'api_discovery', config: {} },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Discovery scan started successfully');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start scan');
    } finally {
      setRefreshing(false);
    }
  };

  const handleApprove = async (assetId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/ai-inventory/${assetId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Asset approved successfully');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve asset');
    }
  };

  // Add Asset handlers
  const handleOpenAddDialog = () => {
    setFormData({
      asset_type: 'model',
      name: '',
      description: '',
      version: '',
      provider: '',
      model_identifier: '',
      server_name: '',
      server_identifier: '',
      token_cost: '',
      status: 'active',
      risk_level: 'low'
    });
    setFormErrors({});
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormData({
      asset_type: 'model',
      name: '',
      description: '',
      version: '',
      provider: '',
      model_identifier: '',
      server_name: '',
      server_identifier: '',
      token_cost: '',
      status: 'active',
      risk_level: 'low'
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.asset_type) errors.asset_type = 'Asset type is required';
    if (!formData.provider.trim()) errors.provider = 'Provider is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAsset = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/ai-inventory', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset added successfully');
      handleCloseAddDialog();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add asset');
    }
  };

  // Edit Asset handlers
  const handleOpenEditDialog = (asset: AIAsset) => {
    setSelectedAsset(asset);
    setFormData({
      asset_type: asset.asset_type,
      name: asset.name,
      description: asset.description || '',
      version: asset.version || '',
      provider: asset.provider,
      model_identifier: asset.model_identifier || '',
      server_name: (asset as any).server_name || '',
      server_identifier: (asset as any).server_identifier || '',
      token_cost: (asset as any).token_cost || '',
      status: asset.status,
      risk_level: asset.risk_level
    });
    setFormErrors({});
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedAsset(null);
    setFormErrors({});
  };

  const handleUpdateAsset = async () => {
    if (!validateForm() || !selectedAsset) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/ai-inventory/${selectedAsset.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset updated successfully');
      handleCloseEditDialog();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update asset');
    }
  };

  // View Asset handlers
  const handleOpenViewDialog = (asset: AIAsset) => {
    setSelectedAsset(asset);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedAsset(null);
  };

  // Delete Asset handlers
  const handleOpenDeleteDialog = (asset: AIAsset) => {
    setSelectedAsset(asset);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedAsset(null);
  };

  const handleDeleteAsset = async () => {
    if (!selectedAsset) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/ai-inventory/${selectedAsset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset deleted successfully');
      handleCloseDeleteDialog();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete asset');
    }
  };

  const getRiskColor = (risk: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (risk?.toLowerCase()) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'deprecated':
        return 'warning';
      case 'testing':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Enhanced MetricCard
  // const MetricCard = ({
  //   title,
  //   value,
  //   icon,
  //   color,
  //   subtitle
  // }: {
  //   title: string;
  //   value: number;
  //   icon: React.ReactNode;
  //   color: string;
  //   subtitle?: string;
  // }) => (
  //   <Fade in timeout={600}>
  //     <Card
  //       elevation={0}
  //       sx={{
  //         height: '100%',
  //         transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  //         border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  //         background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
  //         '&:hover': {
  //           transform: 'translateY(-4px)',
  //           boxShadow: theme.shadows[8],
  //           borderColor: alpha(color, 0.3),
  //         },
  //       }}
  //     >
  //       <CardContent sx={{ p: 3 }}>
  //         <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
  //           <Box flex={1}>
  //             <Typography
  //               variant="body2"
  //               color="text.secondary"
  //               fontWeight={500}
  //               sx={{
  //                 textTransform: 'uppercase',
  //                 letterSpacing: '0.5px',
  //                 fontSize: '0.75rem',
  //                 mb: 1
  //               }}
  //             >
  //               {title}
  //             </Typography>
  //             <Typography
  //               variant="h3"
  //               fontWeight={700}
  //               sx={{
  //                 background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
  //                 WebkitBackgroundClip: 'text',
  //                 WebkitTextFillColor: 'transparent',
  //                 mb: 0.5
  //               }}
  //             >
  //               {value}
  //             </Typography>
  //             {subtitle && (
  //               <Typography
  //                 variant="caption"
  //                 color="text.secondary"
  //               >
  //                 {subtitle}
  //               </Typography>
  //             )}
  //           </Box>
  //           <Box
  //             sx={{
  //               background: `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`,
  //               borderRadius: 2.5,
  //               p: 1.5,
  //               display: 'flex',
  //               alignItems: 'center',
  //               justifyContent: 'center',
  //             }}
  //           >
  //             {icon}
  //           </Box>
  //         </Box>
  //       </CardContent>
  //     </Card>
  //   </Fade>
  // );

  // Loading state
  if (loading && !stats) {
    return (
      <Box>
        <Box mb={4}>
          <Skeleton variant="text" width={400} height={48} />
          <Skeleton variant="text" width={600} height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const paginatedAssets = assets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2.5}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
                }}
              >
                <Inventory2 sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  AI Inventory & Asset Management
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Security sx={{ fontSize: 16 }} />
                  Discover, track, and manage all AI assets across your organization
                  {refreshing && <CircularProgress size={14} thickness={6} />}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Tooltip title="Add New Asset">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddDialog}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Add Asset
                </Button>
              </Tooltip>
              <Tooltip title="Run Auto-Discovery Scan">
                <Button
                  variant="outlined"
                  startIcon={<ScannerIcon />}
                  onClick={handleRunScan}
                  disabled={refreshing}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderWidth: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {refreshing ? 'Scanning...' : 'Run Scan'}
                </Button>
              </Tooltip>
              <Tooltip title="Refresh Data">
                <IconButton
                  onClick={fetchData}
                  disabled={loading}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'rotate(180deg)'
                    },
                    transition: 'all 0.5s ease'
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Fade>

      {/* Enhanced Dashboard Stats */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Total Assets',
              value: stats.totalAssets,
              icon: <Inventory2 sx={{ fontSize: 32 }} />,
              color: '#6366f1',
              subtitle: 'All AI assets',
              delay: 0
            },
            {
              title: 'Unapproved Assets',
              value: stats.unapprovedAssets,
              icon: <WarningIcon sx={{ fontSize: 32 }} />,
              color: '#ff9800',
              subtitle: 'Awaiting approval',
              delay: 100
            },
            {
              title: 'Asset Types',
              value: stats.assetsByType.length,
              icon: <Assessment sx={{ fontSize: 32 }} />,
              color: '#9c27b0',
              subtitle: 'Different categories',
              delay: 200
            },
            {
              title: 'High Risk Assets',
              value: stats.assetsByRisk.find((r) => r.risk_level === 'high')?.count || 0,
              icon: <ErrorOutline sx={{ fontSize: 32 }} />,
              color: '#f44336',
              subtitle: 'Requires attention',
              delay: 300
            }
          ].map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Zoom in timeout={600} style={{ transitionDelay: `${metric.delay}ms` }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(metric.color, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(metric.color, 0.05)} 0%, ${alpha(metric.color, 0.02)} 100%)`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(metric.color, 0.2)}`,
                      border: `1px solid ${alpha(metric.color, 0.3)}`
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${metric.color} 0%, ${alpha(metric.color, 0.8)} 100%)`,
                          color: 'white',
                          boxShadow: `0 4px 12px ${alpha(metric.color, 0.3)}`
                        }}
                      >
                        {metric.icon}
                      </Box>
                      <Chip
                        label={<Speed sx={{ fontSize: 14 }} />}
                        size="small"
                        sx={{
                          bgcolor: alpha(metric.color, 0.1),
                          color: metric.color,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Typography variant="h4" fontWeight={700} color={metric.color} gutterBottom>
                      {metric.value}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary" gutterBottom>
                      {metric.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {metric.subtitle}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Enhanced Filters */}
      <Fade in timeout={800}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: alpha(theme.palette.background.paper, 0.6)
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <FilterList sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                Filter Assets
              </Typography>
            </Box>
            <Divider sx={{ mb: 3, opacity: 0.6 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Asset Type"
                  value={filters.assetType}
                  onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="model">Model</MenuItem>
                  <MenuItem value="dataset">Dataset</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="pipeline">Pipeline</MenuItem>
                  <MenuItem value="api">API</MenuItem>
                  <MenuItem value="tool">Tool</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="deprecated">Deprecated</MenuItem>
                  <MenuItem value="testing">Testing</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Risk Level"
                  value={filters.riskLevel}
                  onChange={(e) => setFilters({ ...filters, riskLevel: e.target.value })}
                >
                  <MenuItem value="">All Risks</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Approval Status"
                  value={filters.isApproved}
                  onChange={(e) => setFilters({ ...filters, isApproved: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Approved</MenuItem>
                  <MenuItem value="false">Unapproved</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Provider"
                  value={filters.provider}
                  onChange={(e) => setFilters({ ...filters, provider: e.target.value })}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  <MenuItem value="OpenAI">OpenAI</MenuItem>
                  <MenuItem value="Google">Google</MenuItem>
                  <MenuItem value="Anthropic">Anthropic</MenuItem>
                  <MenuItem value="AWS">AWS Bedrock</MenuItem>
                  <MenuItem value="Azure">Azure</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Assets Table */}
      <Fade in timeout={1000}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              AI Assets Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {assets.length} asset{assets.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Asset Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Risk Level</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Usage</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Approved</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                          }}
                        >
                          <Inventory2 sx={{ fontSize: 40, color: 'primary.main' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                          No AI Assets Found
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ maxWidth: 400, textAlign: 'center' }}
                        >
                          Run a discovery scan to automatically detect AI assets in your
                          organization
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<ScannerIcon />}
                          onClick={handleRunScan}
                          sx={{
                            mt: 1,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Run Discovery Scan
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAssets.map((asset, index) => (
                    <TableRow
                      key={asset.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          transform: 'scale(1.001)',
                          borderLeft: `3px solid ${theme.palette.primary.main}`
                        },
                        transition: 'all 0.2s ease',
                        borderLeft: `3px solid transparent`
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {asset.name}
                          </Typography>
                          {asset.model_identifier && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                            >
                              <InfoIcon sx={{ fontSize: 12 }} />
                              {asset.model_identifier}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={asset.asset_type}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderWidth: 1.5
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {asset.provider}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={asset.status}
                          size="small"
                          color={getStatusColor(asset.status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={asset.risk_level}
                          size="small"
                          color={getRiskColor(asset.risk_level)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.1)
                            }}
                          >
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {asset.usage_count}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {asset.is_approved ? (
                          <Tooltip title="Approved">
                            <Chip
                              icon={<ApprovedIcon sx={{ fontSize: 16 }} />}
                              label="Approved"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Unapproved">
                            <Chip
                              icon={<WarningIcon sx={{ fontSize: 16 }} />}
                              label="Pending"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 600 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {!asset.is_approved && (
                            <Tooltip title="Approve Asset">
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() => handleApprove(asset.id)}
                                sx={{
                                  minWidth: '80px',
                                  borderWidth: 1.5,
                                  '&:hover': {
                                    borderWidth: 1.5,
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                Approve
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenViewDialog(asset)}
                              sx={{
                                color: theme.palette.info.main,
                                bgcolor: alpha(theme.palette.info.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Asset">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(asset)}
                              sx={{
                                color: theme.palette.warning.main,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.warning.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Asset">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(asset)}
                              sx={{
                                color: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {assets.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: alpha(theme.palette.background.default, 0.4)
              }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={assets.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </Card>
      </Fade>

      {/* Add Asset Dialog - Enhanced Enterprise UI/UX */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          timeout: 400
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.16), 0 12px 24px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}
                >
                  <AddIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                    Add New AI Asset
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Register a new AI model, dataset, or tool in your inventory
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseAddDialog}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[500], 0.2),
                    transform: 'rotate(90deg)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
          {/* Basic Information Section */}
          <Box mb={4}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary"
              sx={{
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.813rem'
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 16,
                  bgcolor: 'primary.main',
                  borderRadius: 1
                }}
              />
              Basic Information
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={!!formErrors.asset_type}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                >
                  <InputLabel>Asset Type *</InputLabel>
                  <Select
                    value={formData.asset_type}
                    label="Asset Type *"
                    onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                  >
                    <MenuItem value="model">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }}
                        />
                        Model
                      </Box>
                    </MenuItem>
                    <MenuItem value="dataset">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }}
                        />
                        Dataset
                      </Box>
                    </MenuItem>
                    <MenuItem value="agent">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }}
                        />
                        Agent
                      </Box>
                    </MenuItem>
                    <MenuItem value="pipeline">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }}
                        />
                        Pipeline
                      </Box>
                    </MenuItem>
                    <MenuItem value="api">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }}
                        />
                        API
                      </Box>
                    </MenuItem>
                    <MenuItem value="tool">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ec4899' }}
                        />
                        Tool
                      </Box>
                    </MenuItem>
                    <MenuItem value="prompt_template">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#14b8a6' }}
                        />
                        Prompt Template
                      </Box>
                    </MenuItem>
                  </Select>
                  {formErrors.asset_type && (
                    <FormHelperText sx={{ mx: 0, mt: 1 }}>{formErrors.asset_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Asset Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={!!formErrors.name}
                  helperText={formErrors.name || 'Enter a descriptive name for the asset'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Provide a detailed description of the asset's purpose and capabilities..."
                  helperText="Optional: Add context about what this asset does and how it's used"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Provider & Version Section */}
          <Box mb={4}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary"
              sx={{
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.813rem'
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 16,
                  bgcolor: 'primary.main',
                  borderRadius: 1
                }}
              />
              Provider & Version Details
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Provider *"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  error={!!formErrors.provider}
                  helperText={formErrors.provider || 'e.g., OpenAI, Google, Anthropic'}
                  placeholder="Enter provider name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="e.g., 1.0, gpt-4-turbo"
                  helperText="Optional: Specify the version number"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Model Identifier"
                  value={formData.model_identifier}
                  onChange={(e) => setFormData({ ...formData, model_identifier: e.target.value })}
                  placeholder="e.g., gpt-4-turbo-preview, claude-3-opus-20240229"
                  helperText="Optional: Technical identifier used in API calls"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Server Name"
                  value={formData.server_name}
                  onChange={(e) => setFormData({ ...formData, server_name: e.target.value })}
                  placeholder="e.g., prod-api-server-01"
                  helperText="Optional: Server name where asset is hosted"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Server Identifier"
                  value={formData.server_identifier}
                  onChange={(e) => setFormData({ ...formData, server_identifier: e.target.value })}
                  placeholder="e.g., srv-abc123xyz"
                  helperText="Optional: Unique server/instance identifier"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Token Cost (USD)"
                  type="number"
                  value={formData.token_cost}
                  onChange={(e) => setFormData({ ...formData, token_cost: e.target.value })}
                  placeholder="e.g., 0.000002"
                  helperText="Optional: Cost per token in USD"
                  inputProps={{ step: '0.000001', min: '0' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Status & Risk Section */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary"
              sx={{
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.813rem'
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 16,
                  bgcolor: 'primary.main',
                  borderRadius: 1
                }}
              />
              Status & Risk Assessment
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="active">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />
                        Active
                      </Box>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: '2px solid #9ca3af'
                          }}
                        />
                        Inactive
                      </Box>
                    </MenuItem>
                    <MenuItem value="deprecated">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <WarningIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                        Deprecated
                      </Box>
                    </MenuItem>
                    <MenuItem value="testing">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <InfoIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                        Testing
                      </Box>
                    </MenuItem>
                    <MenuItem value="archived">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#6b7280' }}
                        />
                        Archived
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>Current operational status of the asset</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                      }
                    }
                  }}
                >
                  <InputLabel>Risk Level</InputLabel>
                  <Select
                    value={formData.risk_level}
                    label="Risk Level"
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                  >
                    <MenuItem value="low">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Low
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Minimal security concerns
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="medium">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Medium
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Moderate risk level
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="high">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            High
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Requires careful review
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="critical">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#dc2626' }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Critical
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Immediate attention needed
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>Security and compliance risk assessment</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 3,
            bgcolor: alpha(theme.palette.background.default, 0.4),
            borderTop: `2px solid ${alpha(theme.palette.divider, 0.08)}`,
            gap: 1.5
          }}
        >
          <Button
            onClick={handleCloseAddDialog}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 120,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAsset}
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            sx={{
              minWidth: 160,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Add Asset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[24]
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <EditIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>
                Edit AI Asset
              </Typography>
            </Box>
            <IconButton onClick={handleCloseEditDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.asset_type}>
                <InputLabel>Asset Type *</InputLabel>
                <Select
                  value={formData.asset_type}
                  label="Asset Type *"
                  onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                >
                  <MenuItem value="model">Model</MenuItem>
                  <MenuItem value="dataset">Dataset</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="pipeline">Pipeline</MenuItem>
                  <MenuItem value="api">API</MenuItem>
                  <MenuItem value="tool">Tool</MenuItem>
                  <MenuItem value="prompt_template">Prompt Template</MenuItem>
                </Select>
                {formErrors.asset_type && <FormHelperText>{formErrors.asset_type}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Asset Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Provider *"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                error={!!formErrors.provider}
                helperText={formErrors.provider}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Model Identifier"
                value={formData.model_identifier}
                onChange={(e) => setFormData({ ...formData, model_identifier: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Server Name"
                value={formData.server_name}
                onChange={(e) => setFormData({ ...formData, server_name: e.target.value })}
                placeholder="e.g., prod-api-server-01"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Server Identifier"
                value={formData.server_identifier}
                onChange={(e) => setFormData({ ...formData, server_identifier: e.target.value })}
                placeholder="e.g., srv-abc123xyz"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Token Cost (USD)"
                type="number"
                value={formData.token_cost}
                onChange={(e) => setFormData({ ...formData, token_cost: e.target.value })}
                placeholder="e.g., 0.000002"
                inputProps={{ step: '0.000001', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="deprecated">Deprecated</MenuItem>
                  <MenuItem value="testing">Testing</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={formData.risk_level}
                  label="Risk Level"
                  onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseEditDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAsset}
            variant="contained"
            startIcon={<EditIcon />}
            color="warning"
          >
            Update Asset
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Asset Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[24]
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <ViewIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>
                Asset Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseViewDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          {selectedAsset && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}
                >
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {selectedAsset.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAsset.description || 'No description provided'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Asset Type
                </Typography>
                <Typography variant="body1" fontWeight={500} mt={0.5}>
                  <Chip label={selectedAsset.asset_type} size="small" variant="outlined" />
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Provider
                </Typography>
                <Typography variant="body1" fontWeight={500} mt={0.5}>
                  {selectedAsset.provider}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Version
                </Typography>
                <Typography variant="body1" fontWeight={500} mt={0.5}>
                  {selectedAsset.version || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Model Identifier
                </Typography>
                <Typography variant="body1" fontWeight={500} mt={0.5}>
                  {selectedAsset.model_identifier || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Status
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={selectedAsset.status}
                    size="small"
                    color={getStatusColor(selectedAsset.status)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Risk Level
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={selectedAsset.risk_level}
                    size="small"
                    color={getRiskColor(selectedAsset.risk_level)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Approval Status
                </Typography>
                <Box mt={0.5}>
                  {selectedAsset.is_approved ? (
                    <Chip icon={<ApprovedIcon />} label="Approved" size="small" color="success" />
                  ) : (
                    <Chip icon={<WarningIcon />} label="Unapproved" size="small" color="warning" />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Usage Count
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.5} color="primary">
                  {selectedAsset.usage_count}
                </Typography>
              </Grid>

              {selectedAsset.last_used && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Last Used
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {new Date(selectedAsset.last_used).toLocaleString()}
                  </Typography>
                </Grid>
              )}

              {selectedAsset.Owner && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Owner
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {selectedAsset.Owner.email}
                  </Typography>
                </Grid>
              )}

              {selectedAsset.Team && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Team
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {selectedAsset.Team.name}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseViewDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[24]
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <ErrorOutline sx={{ color: theme.palette.error.main, fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Delete Asset
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The asset will be permanently deleted.
          </Alert>
          {selectedAsset && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this asset?
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {selectedAsset.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedAsset.provider} • {selectedAsset.asset_type}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAsset}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Asset
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIInventory;
